import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    ListItemButton,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Switch,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddBoxIcon from '@mui/icons-material/AddBox';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InboxIcon from '@mui/icons-material/Inbox';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import FlagIcon from '@mui/icons-material/Flag';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import HubIcon from '@mui/icons-material/Hub';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import CategoryIcon from '@mui/icons-material/Category';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
    useTestConfigurations,
    useCreateTestConfig,
    useUpdateTestConfig,
    useDeleteTestConfig,
    useMigrateTestConfigCategories,
    useMigrateTestConfigIcons,
    useMigrateTestConfigEntitlements,
    useMigrateTestConfigMediaFiles,
} from '../../hooks/testConfigs';
import {
    kAllowedTestValues,
    kCardColors,
    kTestConfigurationStatuses,
} from '../../testConfigConstants';
import mediaFilesService from '../../api/mediaFilesService';
import MediaFilePicker from './MediaFilePicker';
import TestScriptPicker from './TestScriptPicker';
import { useTestCategories } from '../../hooks/testCategories';
import { useDeviceSensors } from '../../hooks/deviceSensors';
import { usePostureInstructions } from '../../hooks/postureInstructions';
import { useEntitlements } from '../../hooks/entitlements';
import { useDisclaimers } from '../../hooks/disclaimers';
import type {
    TestConfiguration,
    TestCategory,
    TestConfigurationStatus,
    CreateTestConfigurationDto,
    UpdateTestConfigurationDto,
} from '../../types/types';
import { t } from '../../i18n/t';

const IconBadge: React.FC<{
    mediaFileId?: number | null;
    iconSvg?: string | null;
    iconColor?: string | null;
    size?: number;
}> = ({ mediaFileId, iconSvg, iconColor, size = 32 }) => {
    const color = iconColor || '#4B9FFF';
    const src = mediaFilesService.resolveFileUrl(mediaFileId, iconSvg);
    return (
        <Box
            sx={{
                width: size * 1.75,
                height: size * 1.75,
                borderRadius: '50%',
                bgcolor: `${color}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {src ? (
                <Box
                    component="img"
                    src={src}
                    alt=""
                    sx={{ width: size, height: size, objectFit: 'contain' }}
                />
            ) : (
                <HealthAndSafetyIcon sx={{ fontSize: size, color }} />
            )}
        </Box>
    );
};

const DashboardTestConfigs = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isWide = useMediaQuery(theme.breakpoints.up(1000));
    const [selected, setSelected] = useState<TestConfiguration | null>(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [createCategoryId, setCreateCategoryId] = useState<number | null>(null);
    const [editing, setEditing] = useState<TestConfiguration | null>(null);
    const [copyFrom, setCopyFrom] = useState<TestConfiguration | null>(null);

    const { data: configs = [], isLoading, refetch } = useTestConfigurations();
    const { data: testCategories = [] } = useTestCategories();
    const migrateMutation = useMigrateTestConfigCategories();
    const migrateIconsMutation = useMigrateTestConfigIcons();
    const migrateEntitlementsMutation = useMigrateTestConfigEntitlements();
    const migrateMediaFilesMutation = useMigrateTestConfigMediaFiles();
    const createMutation = useCreateTestConfig();
    const updateMutation = useUpdateTestConfig();
    const deleteMutation = useDeleteTestConfig();

    const error =
        createMutation.error || updateMutation.error || deleteMutation.error
            ? String(
                  (createMutation.error as Error)?.message ||
                      (updateMutation.error as Error)?.message ||
                      (deleteMutation.error as Error)?.message ||
                      t('testConfigs.error'),
              )
            : null;

    const needsMigration = useMemo(
        () => configs.some((c) => !c.categoryId && !!c.categoryName?.trim()),
        [configs],
    );

    const needsIconsMigration = useMemo(
        () =>
            configs.some((c) => c.needsIconsMigration) ||
            testCategories.some((c) => c.needsIconsMigration),
        [configs, testCategories],
    );

    const needsMediaFileMigration = useMemo(
        () =>
            configs.some((c) => c.needsMediaFileMigration) ||
            testCategories.some((c) => c.needsMediaFileMigration),
        [configs, testCategories],
    );

    const needsEntitlementsMigration = useMemo(
        () =>
            configs.some((c) => c.needsEntitlementsMigration) ||
            testCategories.some((c) => c.needsEntitlementsMigration),
        [configs, testCategories],
    );

    const categoryGroups = useMemo(() => {
        const uncategorized: TestConfiguration[] = [];
        const byCategoryId = new Map<number, TestConfiguration[]>();

        for (const c of configs) {
            if (c.categoryId == null) {
                uncategorized.push(c);
            } else {
                const list = byCategoryId.get(c.categoryId) ?? [];
                list.push(c);
                byCategoryId.set(c.categoryId, list);
            }
        }

        const sortItems = (items: TestConfiguration[]) =>
            [...items].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));

        const groups: Array<{
            categoryId: number | null;
            name: string;
            sortOrder: number;
            items: TestConfiguration[];
        }> = [];

        const sortedCats = [...testCategories].sort(
            (a, b) => a.sortOrder - b.sortOrder || a.id - b.id,
        );
        const seen = new Set<number>();

        for (const cat of sortedCats) {
            const items = byCategoryId.get(cat.id);
            if (items?.length) {
                groups.push({
                    categoryId: cat.id,
                    name: cat.name,
                    sortOrder: cat.sortOrder,
                    items: sortItems(items),
                });
                seen.add(cat.id);
            }
        }

        for (const [catId, items] of Array.from(byCategoryId.entries())) {
            if (!seen.has(catId) && items.length) {
                groups.push({
                    categoryId: catId,
                    name: items[0]?.categoryName ?? `#${catId}`,
                    sortOrder: 9998,
                    items: sortItems(items),
                });
            }
        }

        if (uncategorized.length) {
            groups.push({
                categoryId: null,
                name: t('testConfigs.uncategorized'),
                sortOrder: 9999,
                items: sortItems(uncategorized),
            });
        }

        return groups;
    }, [configs, testCategories]);

    const handleCreate = (categoryId?: number) => {
        setCreateCategoryId(categoryId ?? null);
        setCreateOpen(true);
    };

    const handleMigrateCategories = async () => {
        try {
            const result = await migrateMutation.mutateAsync();
            alert(
                t('testConfigs.migrateSuccess', {
                    updated: result.updated,
                    created: result.createdCategories,
                }),
            );
        } catch (e) {
            console.error(e);
            alert(t('testConfigs.error'));
        }
    };

    const handleMigrateIcons = async () => {
        try {
            const result = await migrateIconsMutation.mutateAsync();
            const unmatchedDetails = result.unmatched?.slice(0, 10).join('\n');
            const unmatchedNote =
                result.unmatched?.length > 0
                    ? `\n\n${t('testConfigs.migrateIconsUnmatched', { count: result.unmatched.length })}${
                          unmatchedDetails ? `\n${unmatchedDetails}` : ''
                      }`
                    : '';
            alert(
                t('testConfigs.migrateIconsSuccess', {
                    imported: result.imported,
                    linkedConfigs: result.linkedConfigs,
                    linkedCategories: result.linkedCategories,
                }) + unmatchedNote,
            );
        } catch (e) {
            console.error(e);
            alert(t('testConfigs.error'));
        }
    };

    const handleMigrateEntitlements = async () => {
        try {
            const result = await migrateEntitlementsMutation.mutateAsync();
            alert(
                t('testConfigs.migrateEntitlementsSuccess', {
                    created: result.createdEntitlements,
                    linkedConfigs: result.linkedConfigs,
                    linkedCategories: result.linkedCategories,
                }),
            );
        } catch (e) {
            console.error(e);
            alert(t('testConfigs.error'));
        }
    };

    const handleMigrateMediaFiles = async () => {
        try {
            const result = await migrateMediaFilesMutation.mutateAsync();
            alert(
                t('testConfigs.migrateMediaFilesSuccess', {
                    migrated: result.migratedRows,
                    linkedPostures: result.linkedPostures,
                    linkedConfigs: result.linkedConfigs,
                    linkedCategories: result.linkedCategories,
                }),
            );
        } catch (e) {
            console.error(e);
            alert(t('testConfigs.error'));
        }
    };

    const handleDelete = async (c: TestConfiguration) => {
        if (!window.confirm(t('testConfigs.deleteConfirm', { name: c.name }))) return;
        try {
            await deleteMutation.mutateAsync(c.id);
            if (selected?.id === c.id) setSelected(null);
        } catch (e) {
            console.error(e);
        }
    };

    const isBusy =
        createMutation.isPending ||
        updateMutation.isPending ||
        deleteMutation.isPending;

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                    {t('testConfigs.title')}
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleCreate()}
                    disabled={isBusy}
                >
                    {t('testConfigs.new')}
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<AddBoxIcon />}
                    onClick={() => navigate('/dashboard/test-categories')}
                    disabled={isBusy}
                >
                    {t('testConfigs.manageCategories')}
                </Button>
                <IconButton onClick={() => refetch()} disabled={isBusy} title={t('testConfigs.refresh')}>
                    <RefreshIcon />
                </IconButton>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => createMutation.reset()}>
                    {error}
                </Alert>
            )}

            {needsMigration && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={handleMigrateCategories}
                            disabled={migrateMutation.isPending}
                        >
                            {t('testConfigs.migrateCategories')}
                        </Button>
                    }
                >
                    {t('testConfigs.migrateCategoriesHint')}
                </Alert>
            )}

            {needsIconsMigration && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={handleMigrateIcons}
                            disabled={migrateIconsMutation.isPending}
                        >
                            {t('testConfigs.migrateIcons')}
                        </Button>
                    }
                >
                    {t('testConfigs.migrateIconsHint')}
                </Alert>
            )}

            {needsEntitlementsMigration && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={handleMigrateEntitlements}
                            disabled={migrateEntitlementsMutation.isPending}
                        >
                            {t('testConfigs.migrateEntitlements')}
                        </Button>
                    }
                >
                    {t('testConfigs.migrateEntitlementsHint')}
                </Alert>
            )}

            {needsMediaFileMigration && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={handleMigrateMediaFiles}
                            disabled={migrateMediaFilesMutation.isPending}
                        >
                            {t('testConfigs.migrateMediaFiles')}
                        </Button>
                    }
                >
                    {t('testConfigs.migrateMediaFilesHint')}
                </Alert>
            )}

            {configs.length === 0 ? (
                <Paper
                    sx={{
                        p: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: 1,
                    }}
                >
                    <InboxIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        {t('testConfigs.noConfigs')}
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleCreate()}>
                        {t('testConfigs.createFirst')}
                    </Button>
                </Paper>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        minHeight: 0,
                        gap: 2,
                        flexDirection: isWide ? 'row' : 'column',
                    }}
                >
                    <Box
                        sx={{
                            flex: isWide ? 2 : 'none',
                            minWidth: 0,
                            overflow: 'auto',
                        }}
                    >
                        {categoryGroups.map((cat) => (
                            <Accordion
                                key={cat.categoryId ?? 'uncategorized'}
                                defaultExpanded
                                sx={{ mb: 1 }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}
                                >
                                    <Typography variant="subtitle1" sx={{ flex: 1 }}>
                                        {cat.name}
                                    </Typography>
                                    <Chip label={cat.items.length} size="small" sx={{ mr: 1 }} />
                                    {cat.categoryId != null && (
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleCreate(cat.categoryId!);
                                            }}
                                            disabled={isBusy}
                                        >
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0 }}>
                                    {cat.items.map((config) => {
                                        const accentColor = config.iconColor || '#4B9FFF';
                                        const iconPath = mediaFilesService.resolveFileUrl(config.mediaFileId, config.iconSvg);
                                        return (
                                            <Paper
                                                key={config.id}
                                                variant="outlined"
                                                sx={{
                                                    mb: 1,
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <ListItemButton
                                                    selected={selected?.id === config.id}
                                                    onClick={() => setSelected(config)}
                                                    sx={{ py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 32,
                                                            height: 32,
                                                            flexShrink: 0,
                                                            borderRadius: '50%',
                                                            bgcolor: `${accentColor}20`,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        {iconPath ? (
                                                            <Box
                                                                component="img"
                                                                src={iconPath}
                                                                alt=""
                                                                sx={{ width: 20, height: 20 }}
                                                            />
                                                        ) : (
                                                            <HealthAndSafetyIcon
                                                                sx={{ fontSize: 20, color: accentColor }}
                                                            />
                                                        )}
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                                        <Typography variant="body2" noWrap>
                                                            {config.name}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{
                                                                display: 'block',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {config.description}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        {config.status && config.status !== 'active' && (
                                                            <Chip
                                                                label={t(`testConfigs.status.${config.status}`)}
                                                                size="small"
                                                                sx={{ height: 20, fontSize: 10 }}
                                                            />
                                                        )}
                                                        <Chip
                                                        label={config.isEnabled ? t('testConfigs.chipActive') : t('testConfigs.chipDisabled')}
                                                        size="small"
                                                        color={config.isEnabled ? 'success' : 'error'}
                                                        sx={{ height: 20, fontSize: 10 }}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setEditing(config);
                                                        }}
                                                        title={t('testConfigs.edit')}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setCopyFrom(config);
                                                        }}
                                                        title={t('testConfigs.copy')}
                                                    >
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(config);
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                    </Box>
                                                </ListItemButton>
                                            </Paper>
                                        );
                                    })}
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>

                    <Box
                        sx={{
                            flex: isWide ? 3 : 'none',
                            minWidth: 0,
                            overflow: 'auto',
                        }}
                    >
                        <TestConfigurationDetails
                            config={selected}
                            testCategories={testCategories}
                            onEdit={() => selected && setEditing(selected)}
                            onCopy={() => selected && setCopyFrom(selected)}
                        />
                    </Box>
                </Box>
            )}

            {createOpen && (
                <ConfigFormDialog
                    initialCategoryId={createCategoryId ?? undefined}
                    onClose={() => {
                        setCreateOpen(false);
                        setCreateCategoryId(null);
                        createMutation.reset();
                    }}
                    onSaved={() => {
                        setCreateOpen(false);
                        setCreateCategoryId(null);
                        createMutation.reset();
                    }}
                    save={async (dto) => {
                        await createMutation.mutateAsync(dto as CreateTestConfigurationDto);
                    }}
                    saving={createMutation.isPending}
                />
            )}
            {editing && (
                <ConfigFormDialog
                    config={editing}
                    onClose={() => {
                        setEditing(null);
                        updateMutation.reset();
                    }}
                    onSaved={() => {
                        setEditing(null);
                        updateMutation.reset();
                        refetch();
                    }}
                    save={async (dto) => {
                        await updateMutation.mutateAsync({ id: editing.id, dto });
                    }}
                    saving={updateMutation.isPending}
                />
            )}
            {copyFrom && (
                <ConfigFormDialog
                    copyFrom={copyFrom}
                    initialCategoryId={copyFrom.categoryId ?? undefined}
                    onClose={() => {
                        setCopyFrom(null);
                        createMutation.reset();
                    }}
                    onSaved={() => {
                        setCopyFrom(null);
                        createMutation.reset();
                    }}
                    save={async (dto) => {
                        await createMutation.mutateAsync(dto as CreateTestConfigurationDto);
                    }}
                    saving={createMutation.isPending}
                />
            )}

        </Box>
    );
};

const TestConfigurationDetails: React.FC<{
    config: TestConfiguration | null;
    testCategories: TestCategory[];
    onEdit: () => void;
    onCopy: () => void;
}> = ({
    config,
    testCategories,
    onEdit,
    onCopy,
}) => {
    const { data: disclaimers = [] } = useDisclaimers();
    if (!config) {
        return (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">{t('testConfigs.selectConfig')}</Typography>
            </Paper>
        );
    }

    const color = config.iconColor || '#4B9FFF';
    const linkedCategory =
        config.categoryId != null
            ? testCategories.find((c) => c.id === config.categoryId)
            : undefined;

    const Section: React.FC<{ title: string; value?: string | null; icon: React.ReactNode }> = ({
        title,
        value,
        icon,
    }) =>
        value ? (
            <Box sx={{ py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ color: 'primary.main', mt: 0.25 }}>{icon}</Box>
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                            {title}
                        </Typography>
                        <Typography variant="body2">{value}</Typography>
                    </Box>
                </Box>
            </Box>
        ) : null;

    return (
        <Paper sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <IconBadge
                    mediaFileId={config.mediaFileId}
                    iconSvg={config.iconSvg}
                    iconColor={config.iconColor}
                    size={32}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6">{config.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {config.description}
                    </Typography>
                </Box>
                <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={onEdit}>
                    {t('testConfigs.edit')}
                </Button>
                <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />} onClick={onCopy}>
                    {t('testConfigs.copy')}
                </Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip label={t('testConfigs.durationChip', { n: config.duration })} size="small" />
                <Chip label={t('testConfigs.stagesChip', { n: config.stages ?? 1 })} size="small" />
                <Chip
                    label={config.isEnabled ? t('testConfigs.chipActive') : t('testConfigs.chipDisabled')}
                    size="small"
                    color={config.isEnabled ? 'success' : 'error'}
                />
                {config.status && config.status !== 'active' && (
                    <Chip label={t(`testConfigs.status.${config.status}`)} size="small" />
                )}
            </Box>

            <Section title="Goal" value={config.goal} icon={<FlagIcon fontSize="small" />} />
            <Section title="Output" value={config.output} icon={<AnalyticsIcon fontSize="small" />} />
            <Section title="Posture" value={config.posture} icon={<AccessibilityNewIcon fontSize="small" />} />
            <Section title="Sensors" value={config.sensors} icon={<HubIcon fontSize="small" />} />
            <Section title="Why it matters" value={config.whyMatters} icon={<InfoOutlinedIcon fontSize="small" />} />
            <Section title="How it works" value={config.howWorks} icon={<SettingsSuggestIcon fontSize="small" />} />

            {(config.testValues?.length ?? 0) > 0 && (
                <Box sx={{ py: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Test metrics
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {config.testValues!.map((v) => (
                            <Chip key={v} label={v} size="small" sx={{ bgcolor: `${color}20` }} />
                        ))}
                    </Box>
                </Box>
            )}

            {(config.resultValues?.length ?? 0) > 0 && (
                <Box sx={{ py: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Result metrics
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {config.resultValues!.map((v) => (
                            <Chip key={v} label={v} size="small" sx={{ bgcolor: `${color}20` }} />
                        ))}
                    </Box>
                </Box>
            )}

            {(config.disclaimerIds?.length ?? 0) > 0 && (
                <Box sx={{ py: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {t('testConfigs.disclaimerIds')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {config.disclaimerIds!.map((id) => {
                            const disclaimer = disclaimers.find((item) => item.id === id);
                            return <Chip key={id} label={disclaimer?.code ?? `#${id}`} size="small" />;
                        })}
                    </Box>
                </Box>
            )}

            {linkedCategory ? (
                <Box sx={{ py: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ color: 'primary.main', mt: 0.25 }}>
                            <CategoryIcon fontSize="small" />
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                                {t('testConfigs.categoryId')}
                            </Typography>
                            <Typography variant="body2">
                                {linkedCategory.name} ({linkedCategory.type})
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            ) : config.categoryName ? (
                <Box sx={{ py: 1 }}>
                    <Alert severity="warning" sx={{ py: 0 }}>
                        {t('testConfigs.categoryUnlinked', { name: config.categoryName })}
                    </Alert>
                </Box>
            ) : null}

            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('testConfigs.script')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('testConfigs.pdfScript')}:
                        </Typography>
                        <Chip
                            label={config.pdfScript?.name ?? t('testConfigs.noScript')}
                            size="small"
                            color={config.pdfScript ? 'success' : 'default'}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('testConfigs.indicatorsScript')}:
                        </Typography>
                        <Chip
                            label={config.indicatorsScript?.name ?? t('testConfigs.noScript')}
                            size="small"
                            color={config.indicatorsScript ? 'success' : 'default'}
                        />
                    </Box>
                    {config.scriptPath && (
                        <Chip label={`${t('testConfigs.scriptPresent')} (legacy)`} size="small" />
                    )}
                </Box>
            </Box>
        </Paper>
    );
};

const FormTwoCol: React.FC<{ isFormWide: boolean; children: [React.ReactNode, React.ReactNode] }> = ({
    isFormWide,
    children,
}) =>
    isFormWide ? (
        <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>{children[0]}</Box>
            <Box sx={{ flex: 1 }}>{children[1]}</Box>
        </Box>
    ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {children[0]}
            {children[1]}
        </Box>
    );

const FormField: React.FC<{
    label: string;
    value: string;
    onChange: (v: string) => void;
    multiline?: boolean;
    onOpenFullscreen?: () => void;
}> = ({ label, value, onChange, multiline, onOpenFullscreen }) => (
    <TextField
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        fullWidth
        multiline={multiline}
        minRows={multiline ? 3 : 1}
        InputProps={
            onOpenFullscreen
                ? {
                      endAdornment: (
                          <InputAdornment position="end">
                              <IconButton size="small" onClick={onOpenFullscreen}>
                                  <OpenInFullIcon fontSize="small" />
                              </IconButton>
                          </InputAdornment>
                      ),
                  }
                : undefined
        }
    />
);

interface ConfigFormDialogProps {
    config?: TestConfiguration;
    copyFrom?: TestConfiguration;
    initialCategoryId?: number;
    onClose: () => void;
    onSaved: () => void;
    save: (dto: CreateTestConfigurationDto | UpdateTestConfigurationDto) => Promise<void>;
    saving: boolean;
}

const ConfigFormDialog: React.FC<ConfigFormDialogProps> = ({
    config,
    copyFrom,
    initialCategoryId,
    onClose,
    onSaved,
    save,
    saving,
}) => {
    const theme = useTheme();
    const isFormWide = useMediaQuery(theme.breakpoints.up(720));
    const { data: testCategories = [] } = useTestCategories();
    const { data: deviceSensors = [] } = useDeviceSensors();
    const { data: postureInstructions = [] } = usePostureInstructions();
    const { data: catalogEntitlements = [] } = useEntitlements();
    const { data: disclaimers = [] } = useDisclaimers();

    const source = config ?? copyFrom;
    const [name, setName] = useState(() =>
        copyFrom ? `${copyFrom.name}${t('testConfigs.copySuffix')}` : (config?.name ?? ''),
    );
    const [configType, setConfigType] = useState(source?.type ?? '');
    const [description, setDescription] = useState(source?.description ?? '');
    const [duration, setDuration] = useState(source?.duration ?? 60);
    const [isEnabled, setIsEnabled] = useState(source?.isEnabled ?? true);
    const [stages, setStages] = useState(source?.stages ?? 1);
    const [categoryId, setCategoryId] = useState<number | ''>(
        source?.categoryId ?? initialCategoryId ?? '',
    );
    const [index, setIndex] = useState(source?.index ?? 0);
    const [sensorIds, setSensorIds] = useState<number[]>(source?.sensorIds ?? []);
    const [postureInstructionIds, setPostureInstructionIds] = useState<number[]>(
        source?.postureInstructionIds ?? [],
    );
    const [status, setStatus] = useState<TestConfigurationStatus>(source?.status ?? 'active');
    const [entitlementIds, setEntitlementIds] = useState<number[]>(source?.entitlementIds ?? []);
    const [disclaimerIds, setDisclaimerIds] = useState<number[]>(source?.disclaimerIds ?? []);
    const [metadataJson, setMetadataJson] = useState(
        JSON.stringify(source?.metadata ?? {}, null, 2),
    );
    const [mediaFileId, setMediaFileId] = useState<number | null>(source?.mediaFileId ?? null);
    const [instructionMediaFileId, setInstructionMediaFileId] = useState<number | null>(
        source?.instructionMediaFileId ?? null,
    );
    const [pdfScriptId, setPdfScriptId] = useState<number | null>(source?.pdfScriptId ?? null);
    const [indicatorsScriptId, setIndicatorsScriptId] = useState<number | null>(
        source?.indicatorsScriptId ?? null,
    );
    const [iconColor, setIconColor] = useState(source?.iconColor ?? kCardColors[0].hex);
    const [goal, setGoal] = useState(source?.goal ?? '');
    const [sensors, setSensors] = useState(source?.sensors ?? '');
    const [posture, setPosture] = useState(source?.posture ?? '');
    const [output, setOutput] = useState(source?.output ?? '');
    const [whyMatters, setWhyMatters] = useState(source?.whyMatters ?? '');
    const [howWorks, setHowWorks] = useState(source?.howWorks ?? '');
    const [selectedTestValues, setSelectedTestValues] = useState<Set<string>>(
        () => new Set(source?.testValues ?? []),
    );
    const [selectedResultValues, setSelectedResultValues] = useState<Set<string>>(
        () => new Set(source?.resultValues ?? []),
    );
    const [fullscreenField, setFullscreenField] = useState<{
        label: string;
        value: string;
        setValue: (v: string) => void;
    } | null>(null);

    React.useEffect(() => {
        if (categoryId !== '' || initialCategoryId == null) return;
        setCategoryId(initialCategoryId);
    }, [initialCategoryId, categoryId]);

    const toggleTestValue = (v: string) => {
        setSelectedTestValues((prev) => {
            const next = new Set(prev);
            if (next.has(v)) next.delete(v);
            else next.add(v);
            return next;
        });
    };

    const toggleResultValue = (v: string) => {
        setSelectedResultValues((prev) => {
            const next = new Set(prev);
            if (next.has(v)) next.delete(v);
            else next.add(v);
            return next;
        });
    };

    const handleSave = async () => {
        if (!name.trim()) {
            alert(t('testConfigs.enterName'));
            return;
        }
        if (selectedTestValues.size === 0) {
            alert(t('testConfigs.pickOutputMetric'));
            return;
        }
        if (categoryId === '') {
            alert(t('testConfigs.pickCategory'));
            return;
        }

        let metadata: Record<string, unknown> = {};
        try {
            metadata = metadataJson.trim() ? JSON.parse(metadataJson) : {};
            if (typeof metadata !== 'object' || metadata === null || Array.isArray(metadata)) {
                throw new Error('metadata must be object');
            }
        } catch {
            alert(t('testConfigs.invalidMetadata'));
            return;
        }

        const dto: CreateTestConfigurationDto | UpdateTestConfigurationDto = {
            name: name.trim(),
            type: configType.trim() || undefined,
            description: description.trim(),
            duration: Number(duration),
            isEnabled,
            stages: Number(stages),
            categoryId: Number(categoryId),
            sensorIds,
            postureInstructionIds,
            status,
            entitlementIds,
            disclaimerIds,
            metadata,
            index: Number(index),
            mediaFileId,
            instructionMediaFileId,
            pdfScriptId,
            indicatorsScriptId,
            iconColor: iconColor || undefined,
            goal: goal || undefined,
            sensors: sensors || undefined,
            posture: posture || undefined,
            output: output || undefined,
            whyMatters: whyMatters || undefined,
            howWorks: howWorks || undefined,
            testValues: Array.from(selectedTestValues),
            resultValues: Array.from(selectedResultValues),
        };

        try {
            await save(dto);
            onSaved();
        } catch (e) {
            console.error(e);
            alert(t('testConfigs.saveFailed'));
        }
    };

    return (
        <>
            <Dialog open fullWidth maxWidth="md" onClose={onClose}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {config ? t('testConfigs.dialogEdit') : copyFrom ? t('testConfigs.dialogCopy') : t('testConfigs.dialogNew')}
                    <IconButton onClick={onClose} disabled={saving} size="small">
                        <ClearIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <FormControlLabel
                            control={<Switch checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} />}
                            label={t('testConfigs.enabled')}
                        />

                        <FormTwoCol isFormWide={isFormWide}>
                            <FormField label={t('testConfigs.fieldName')} value={name} onChange={setName} />
                            <FormField label={t('testConfigs.fieldType')} value={configType} onChange={setConfigType} />
                        </FormTwoCol>

                        <FormField
                            label={t('testConfigs.fieldDescription')}
                            value={description}
                            onChange={setDescription}
                            multiline
                            onOpenFullscreen={() =>
                                setFullscreenField({ label: t('testConfigs.fieldDescription'), value: description, setValue: setDescription })
                            }
                        />

                        <FormTwoCol isFormWide={isFormWide}>
                            <FormField
                                label={t('testConfigs.fieldGoal')}
                                value={goal}
                                onChange={setGoal}
                                multiline
                                onOpenFullscreen={() =>
                                    setFullscreenField({ label: t('testConfigs.fieldGoal'), value: goal, setValue: setGoal })
                                }
                            />
                            <FormField
                                label={t('testConfigs.fieldSensors')}
                                value={sensors}
                                onChange={setSensors}
                                multiline
                                onOpenFullscreen={() =>
                                    setFullscreenField({ label: t('testConfigs.fieldSensors'), value: sensors, setValue: setSensors })
                                }
                            />
                        </FormTwoCol>

                        <FormTwoCol isFormWide={isFormWide}>
                            <FormField
                                label={t('testConfigs.fieldPosture')}
                                value={posture}
                                onChange={setPosture}
                                multiline
                                onOpenFullscreen={() =>
                                    setFullscreenField({ label: t('testConfigs.fieldPosture'), value: posture, setValue: setPosture })
                                }
                            />
                            <FormField
                                label={t('testConfigs.fieldOutput')}
                                value={output}
                                onChange={setOutput}
                                multiline
                                onOpenFullscreen={() =>
                                    setFullscreenField({
                                        label: t('testConfigs.fieldOutput'),
                                        value: output,
                                        setValue: setOutput,
                                    })
                                }
                            />
                        </FormTwoCol>

                        <FormTwoCol isFormWide={isFormWide}>
                            <FormField
                                label={t('testConfigs.fieldWhy')}
                                value={whyMatters}
                                onChange={setWhyMatters}
                                multiline
                                onOpenFullscreen={() =>
                                    setFullscreenField({
                                        label: t('testConfigs.fieldWhy'),
                                        value: whyMatters,
                                        setValue: setWhyMatters,
                                    })
                                }
                            />
                            <FormField
                                label={t('testConfigs.fieldHow')}
                                value={howWorks}
                                onChange={setHowWorks}
                                multiline
                                onOpenFullscreen={() =>
                                    setFullscreenField({
                                        label: t('testConfigs.fieldHow'),
                                        value: howWorks,
                                        setValue: setHowWorks,
                                    })
                                }
                            />
                        </FormTwoCol>

                        <FormTwoCol isFormWide={isFormWide}>
                            <TextField
                                label={t('testConfigs.durationSec')}
                                type="number"
                                value={duration}
                                onChange={(e) => setDuration(Number(e.target.value) || 0)}
                                size="small"
                                fullWidth
                            />
                            <TextField
                                label={t('testConfigs.stages')}
                                type="number"
                                value={stages}
                                onChange={(e) => setStages(Number(e.target.value) || 1)}
                                size="small"
                                fullWidth
                            />
                        </FormTwoCol>

                        <Typography variant="subtitle2" sx={{ mt: 1 }}>
                            {t('testConfigs.outputMetrics')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {kAllowedTestValues.map((v) => (
                                <Chip
                                    key={v}
                                    label={v}
                                    onClick={() => toggleTestValue(v)}
                                    color={selectedTestValues.has(v) ? 'primary' : 'default'}
                                    variant={selectedTestValues.has(v) ? 'filled' : 'outlined'}
                                    size="small"
                                />
                            ))}
                        </Box>

                        <Typography variant="subtitle2">{t('testConfigs.resultMetrics')}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {kAllowedTestValues.map((v) => (
                                <Chip
                                    key={v}
                                    label={v}
                                    onClick={() => toggleResultValue(v)}
                                    color={selectedResultValues.has(v) ? 'primary' : 'default'}
                                    variant={selectedResultValues.has(v) ? 'filled' : 'outlined'}
                                    size="small"
                                />
                            ))}
                        </Box>

                        <FormTwoCol isFormWide={isFormWide}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>{t('testConfigs.categoryId')}</InputLabel>
                                <Select
                                    label={t('testConfigs.categoryId')}
                                    value={categoryId === '' ? '' : categoryId}
                                    onChange={(e) =>
                                        setCategoryId(e.target.value === '' ? '' : Number(e.target.value))
                                    }
                                >
                                    {testCategories.map((cat) => (
                                        <MenuItem key={cat.id} value={cat.id}>
                                            {cat.name} ({cat.type})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label={t('testConfigs.fieldIndex')}
                                type="number"
                                value={index}
                                onChange={(e) => setIndex(Number(e.target.value) || 0)}
                                size="small"
                                fullWidth
                            />
                        </FormTwoCol>

                        <FormTwoCol isFormWide={isFormWide}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>{t('testConfigs.sensorIds')}</InputLabel>
                                <Select
                                    multiple
                                    label={t('testConfigs.sensorIds')}
                                    value={sensorIds}
                                    onChange={(e) =>
                                        setSensorIds(
                                            typeof e.target.value === 'string'
                                                ? []
                                                : (e.target.value as number[]),
                                        )
                                    }
                                    input={<OutlinedInput label={t('testConfigs.sensorIds')} />}
                                    renderValue={(selected) =>
                                        selected
                                            .map(
                                                (id) =>
                                                    deviceSensors.find((s) => s.id === id)?.type ?? id,
                                            )
                                            .join(', ')
                                    }
                                >
                                    {deviceSensors.map((sensor) => (
                                        <MenuItem key={sensor.id} value={sensor.id}>
                                            <Checkbox checked={sensorIds.includes(sensor.id)} />
                                            <ListItemText
                                                primary={sensor.type}
                                                secondary={sensor.description}
                                            />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" fullWidth>
                                <InputLabel>{t('testConfigs.postureInstructionIds')}</InputLabel>
                                <Select
                                    multiple
                                    label={t('testConfigs.postureInstructionIds')}
                                    value={postureInstructionIds}
                                    onChange={(e) =>
                                        setPostureInstructionIds(
                                            typeof e.target.value === 'string'
                                                ? []
                                                : (e.target.value as number[]),
                                        )
                                    }
                                    input={
                                        <OutlinedInput label={t('testConfigs.postureInstructionIds')} />
                                    }
                                    renderValue={(selected) =>
                                        [...selected]
                                            .sort((a, b) => {
                                                const oa =
                                                    postureInstructions.find((p) => p.id === a)?.order ??
                                                    0;
                                                const ob =
                                                    postureInstructions.find((p) => p.id === b)?.order ??
                                                    0;
                                                return oa - ob || a - b;
                                            })
                                            .map(
                                                (id) =>
                                                    postureInstructions.find((p) => p.id === id)
                                                        ?.title ?? id,
                                            )
                                            .join(', ')
                                    }
                                >
                                    {[...postureInstructions]
                                        .sort((a, b) => a.order - b.order || a.id - b.id)
                                        .map((instr) => (
                                            <MenuItem key={instr.id} value={instr.id}>
                                                <Checkbox
                                                    checked={postureInstructionIds.includes(instr.id)}
                                                />
                                                <ListItemText
                                                    primary={`${instr.order}. ${instr.title}`}
                                                    secondary={instr.type}
                                                />
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </FormTwoCol>

                        <FormControl size="small" fullWidth>
                            <InputLabel>{t('testConfigs.disclaimerIds')}</InputLabel>
                            <Select
                                multiple
                                label={t('testConfigs.disclaimerIds')}
                                value={disclaimerIds}
                                onChange={(e) =>
                                    setDisclaimerIds(
                                        typeof e.target.value === 'string'
                                            ? []
                                            : (e.target.value as number[]),
                                    )
                                }
                                input={<OutlinedInput label={t('testConfigs.disclaimerIds')} />}
                                renderValue={(selected) =>
                                    selected
                                        .map(
                                            (id) =>
                                                disclaimers.find((item) => item.id === id)?.code ?? id,
                                        )
                                        .join(', ')
                                }
                            >
                                {disclaimers.map((disclaimer) => (
                                    <MenuItem key={disclaimer.id} value={disclaimer.id}>
                                        <Checkbox checked={disclaimerIds.includes(disclaimer.id)} />
                                        <ListItemText
                                            primary={disclaimer.code}
                                            secondary={(disclaimer.body ?? '').replace(/\s+/g, ' ').trim().slice(0, 80)}
                                        />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormTwoCol isFormWide={isFormWide}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>{t('testConfigs.fieldStatus')}</InputLabel>
                                <Select
                                    label={t('testConfigs.fieldStatus')}
                                    value={status}
                                    onChange={(e) =>
                                        setStatus(e.target.value as TestConfigurationStatus)
                                    }
                                >
                                    {kTestConfigurationStatuses.map((s) => (
                                        <MenuItem key={s} value={s}>
                                            {t(`testConfigs.status.${s}`)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" fullWidth>
                                <InputLabel>{t('testConfigs.entitlementIds')}</InputLabel>
                                <Select
                                    multiple
                                    label={t('testConfigs.entitlementIds')}
                                    value={entitlementIds}
                                    onChange={(e) =>
                                        setEntitlementIds(
                                            typeof e.target.value === 'string'
                                                ? []
                                                : (e.target.value as number[]),
                                        )
                                    }
                                    input={<OutlinedInput label={t('testConfigs.entitlementIds')} />}
                                    renderValue={(selected) =>
                                        selected
                                            .map(
                                                (id) =>
                                                    catalogEntitlements.find((e) => e.id === id)
                                                        ?.type ?? id,
                                            )
                                            .join(', ')
                                    }
                                >
                                    {catalogEntitlements.map((entitlement) => (
                                        <MenuItem key={entitlement.id} value={entitlement.id}>
                                            <Checkbox checked={entitlementIds.includes(entitlement.id)} />
                                            <ListItemText
                                                primary={entitlement.type}
                                                secondary={entitlement.description}
                                            />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FormTwoCol>

                        <TextField
                            label={t('testConfigs.metadata')}
                            value={metadataJson}
                            onChange={(e) => setMetadataJson(e.target.value)}
                            multiline
                            minRows={3}
                            size="small"
                            fullWidth
                            placeholder="{}"
                        />

                        <Typography variant="subtitle2">{t('testConfigs.iconColor')}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <TextField
                                value={iconColor}
                                onChange={(e) => setIconColor(e.target.value)}
                                size="small"
                                sx={{ width: 120 }}
                                placeholder="#RRGGBB"
                            />
                            {kCardColors.map((c) => (
                                <Chip
                                    key={c.hex}
                                    label={c.name}
                                    onClick={() => setIconColor(c.hex)}
                                    sx={{
                                        bgcolor: iconColor === c.hex ? c.hex : undefined,
                                        borderColor: c.hex,
                                        border: '2px solid',
                                    }}
                                    size="small"
                                />
                            ))}
                        </Box>

                        <MediaFilePicker
                            labelKey="testConfigs.fieldMediaFile"
                            kind="image"
                            value={mediaFileId}
                            onChange={setMediaFileId}
                        />
                        <MediaFilePicker
                            labelKey="testConfigs.fieldInstructionMedia"
                            kind="av"
                            value={instructionMediaFileId}
                            onChange={setInstructionMediaFileId}
                        />

                        <FormTwoCol isFormWide={isFormWide}>
                            <TestScriptPicker
                                label={t('testConfigs.pdfScript')}
                                purpose="pdf"
                                value={pdfScriptId}
                                onChange={setPdfScriptId}
                            />
                            <TestScriptPicker
                                label={t('testConfigs.indicatorsScript')}
                                purpose="indicators"
                                value={indicatorsScriptId}
                                onChange={setIndicatorsScriptId}
                            />
                        </FormTwoCol>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {config ? t('common.save') : t('testConfigs.create')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!fullscreenField}
                onClose={() => setFullscreenField(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { maxHeight: '80vh' } }}
            >
                {fullscreenField && (
                    <>
                        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            {fullscreenField.label}
                            <IconButton onClick={() => setFullscreenField(null)}>
                                <ClearIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent>
                            <TextField
                                value={fullscreenField.value}
                                onChange={(e) => fullscreenField.setValue(e.target.value)}
                                multiline
                                fullWidth
                                minRows={15}
                                sx={{ mt: 1 }}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setFullscreenField(null)}>{t('common.done')}</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </>
    );
};

export default DashboardTestConfigs;
